import React, { createContext, useContext, useState, useEffect } from 'react';
import { authFetch } from '../utils/api';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (isProduction ? 'https://ST Studio.onrender.com/api' : 'http://localhost:5050/api');

const PermissionsContext = createContext({
  permissions: [],
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  loading: true,
  refreshPermissions: () => { }
});

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUserPermissions = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      console.log('PermissionsContext - Token exists:', !!token);

      if (!token) {
        setPermissions([]);
        setLoading(false);
        return;
      }

      const response = await authFetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('PermissionsContext - Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('PermissionsContext - User permissions:', data.permissions);
        setPermissions(data.permissions || []);
      } else {
        console.error('Failed to fetch permissions - Status:', response.status);
        if (response.status === 401) {
          // Token invalid, clear storage but don't redirect
          // Let ProtectedRoute handle the redirect
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
        }
        setPermissions([]);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserPermissions();

    const handleStorage = () => {
      console.log('PermissionsContext - Storage event detected, refreshing...');
      fetchUserPermissions();
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const hasPermission = (permission) => {
    if (permissions.includes('*')) return true;
    return permissions.includes(permission);
  };

  const hasAnyPermission = (permissionList) => {
    if (permissions.includes('*')) return true;
    return permissionList.some(permission => permissions.includes(permission));
  };

  const hasAllPermissions = (permissionList) => {
    if (permissions.includes('*')) return true;
    return permissionList.every(permission => permissions.includes(permission));
  };

  const refreshPermissions = () => {
    fetchUserPermissions();
  };

  const value = {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    loading,
    refreshPermissions
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export default PermissionsContext;